/**
 * useDealCadence - Hook para cadência ativa de um negócio
 *
 * Item 2.11 do checklist Playbook de Vendas.
 *
 * Responsabilidades:
 * - Carrega via RPC `get_deal_next_action` a próxima atividade pendente.
 * - Carrega via RPC `list_deal_cadence_steps` a lista completa de passos.
 * - Expõe mutações: completar, pular, cancelar cadência.
 * - Subscreve realtime em `crm_deal_cadence_steps` e `crm_deal_cadences`
 *   para invalidar cache automaticamente.
 *
 * @version 1.0.0
 * RELEASE: 2026-05-08 v1.0.0 - Criação do hook
 */

import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { invalidateActivityListCaches } from '@/lib/cache/invalidateActivityListCaches';

const logger = createLogger('useDealCadence');

// Próxima ação retornada pela RPC `get_deal_next_action`
export interface DealNextAction {
  cadence_id: string;
  step_id: string;
  playbook_id: string;
  playbook_name: string;
  activity_type: string;
  title: string;
  description_html: string | null;
  channel: string | null;
  due_at: string | null;
  is_overdue: boolean;
  sort_order: number;
  total_steps: number;
  completed_steps: number;
}

// Step retornado pela RPC `list_deal_cadence_steps`
export interface CadenceStep {
  cadence_id: string;
  cadence_status: string;
  playbook_id: string;
  playbook_name: string;
  /** 'flat' (legado) ou 'per_phase' (agrupado por fase) */
  playbook_scope?: 'flat' | 'per_phase';
  started_at: string | null;
  step_id: string;
  sort_order: number;
  /** Fase à qual o step pertence (somente per_phase) */
  phase_id: string | null;
  activity_type: string;
  title: string;
  description_html: string | null;
  channel: string | null;
  // v1.3.0: inclui 'cancelled'
  step_status: 'pending' | 'in_progress' | 'completed' | 'skipped' | 'cancelled' | 'overdue';
  due_at: string | null;
  completed_at: string | null;
  skipped_at: string | null;
  // v1.3.0: metadados de cancelamento
  cancelled_at?: string | null;
  cancelled_reason?: string | null;
  // v1.3.0: TRUE quando o step foi criado manualmente pelo vendedor
  is_manual?: boolean;
  is_required: boolean;
  is_overdue: boolean;
  // Roteiro (Playbook v2.0)
  script_html?: string | null;
  goal?: string | null;
  success_criteria?: Array<{ id: string; label: string; required: boolean }>;
  objections?: Array<{ id: string; question: string; answer_html: string }>;
  /**
   * Outcome persistido em `crm_deal_cadence_steps.outcome` pela RPC
   * `complete_cadence_step`. Contém os critérios de sucesso efetivamente
   * marcados pelo usuário no momento da conclusão. Usado para hidratar a UI.
   */
  outcome?: {
    success_criteria?: Array<{
      id: string;
      checked: boolean;
      label?: string;
      required?: boolean;
    }>;
  } | null;
}

/**
 * Hook unificado para cadência de um negócio.
 */
export function useDealCadence(dealId: string | null | undefined) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id ?? null;

  // ----- Query: próxima ação ------------------------------------------------
  const nextActionQuery = useQuery({
    queryKey: ['deal-cadence-next', dealId, tenantId],
    queryFn: async (): Promise<DealNextAction | null> => {
      if (!dealId || !tenantId) return null;
      const { data, error } = await (supabase.rpc as any)('get_deal_next_action', {
        p_deal_id: dealId,
        p_tenant_hint: tenantId,
      });
      if (error) {
        logger.error('Erro ao buscar próxima ação:', error);
        throw error;
      }
      const row = Array.isArray(data) ? data[0] : data;
      return row ?? null;
    },
    enabled: !!dealId && !!tenantId,
    staleTime: 15_000,
  });

  // ----- Query: lista completa de steps -------------------------------------
  const stepsQuery = useQuery({
    queryKey: ['deal-cadence-steps', dealId, tenantId],
    queryFn: async (): Promise<CadenceStep[]> => {
      if (!dealId || !tenantId) return [];
      const { data, error } = await (supabase.rpc as any)('list_deal_cadence_steps', {
        p_deal_id: dealId,
        p_tenant_hint: tenantId,
      });
      if (error) {
        logger.error('Erro ao buscar passos da cadência:', error);
        throw error;
      }
      // RPC retorna linha vazia (com step_id NULL) quando não há cadência
      return ((data ?? []) as CadenceStep[]).filter((r) => !!r.step_id);
    },
    enabled: !!dealId && !!tenantId,
    staleTime: 15_000,
  });

  // ----- Realtime: invalida cache em mudanças -------------------------------
  // Sufixo único por instância do hook para evitar colisão no registry interno
  // do supabase-js (StrictMode/remontagem rápida pode reaproveitar canal já
  // subscrito e quebrar com "cannot add postgres_changes after subscribe()").
  const channelUidRef = useRef<string>(
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );

  useEffect(() => {
    if (!dealId || !tenantId) return;

    const channelName = `deal-cadence-${dealId}-${channelUidRef.current}`;
    logger.debug('Subscrevendo Realtime de cadência:', channelName);

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'crm_deal_cadence_steps' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['deal-cadence-next', dealId, tenantId] });
          queryClient.invalidateQueries({ queryKey: ['deal-cadence-steps', dealId, tenantId] });
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'crm_deal_cadences', filter: `deal_id=eq.${dealId}` },
        () => {
          queryClient.invalidateQueries({ queryKey: ['deal-cadence-next', dealId, tenantId] });
          queryClient.invalidateQueries({ queryKey: ['deal-cadence-steps', dealId, tenantId] });
        },
      )
      .subscribe();

    return () => {
      logger.debug('Removendo Realtime de cadência:', channelName);
      supabase.removeChannel(channel);
    };
  }, [dealId, tenantId, queryClient]);

  // ----- Mutações -----------------------------------------------------------
  //
  // RELEASE: 2026-07-25 v1.1.0 - Corrigidas as chaves de invalidação da timeline
  //          do negócio. Antes usava-se ['deal-activities', dealId] (chave
  //          inexistente), o que impedia que eventos como reabertura, edição,
  //          cancelamento e conclusão de step (manual ou de playbook) fossem
  //          refletidos imediatamente na aba Resumo → Atividades Recentes.
  //          Agora todas as mutations invalidam as chaves reais consumidas por
  //          `useDealActivitiesPaginated`: ['crm-deal-activities-paginated',
  //          dealId], ['crm-deal-activities', dealId] e ['crm-activities-feed'].
  //          Também foram adicionadas as invalidações da timeline em
  //          `completeStep`, `skipStep` e `cancelCadence`, que antes não a
  //          atualizavam.

  /**
   * Helper interno: invalida todas as chaves da timeline do negócio.
   * Usado por TODAS as mutations de cadência para garantir que qualquer
   * evento gravado em `crm_deal_activities` pelas RPCs (step_completed,
   * step_reopened, step_cancelled, manual_step_created, manual_step_updated,
   * etc.) apareça imediatamente na aba Resumo do modal do negócio.
   */
  const invalidateDealTimeline = () => {
    // Chave usada por useDealActivitiesPaginated (timeline paginada do modal)
    queryClient.invalidateQueries({ queryKey: ['crm-deal-activities-paginated', dealId] });
    // Chave legada de listagem simples de atividades do negócio
    queryClient.invalidateQueries({ queryKey: ['crm-deal-activities', dealId] });
    // Feed global de atividades (páginas /tenant/activities/feed)
    queryClient.invalidateQueries({ queryKey: ['crm-activities-feed'] });
  };

  // completeStep aceita string (retrocompat) OU { stepId, outcome } para
  // enviar o outcome (ex.: critérios de sucesso marcados) que a RPC persiste
  // na coluna crm_deal_cadence_steps.outcome.
  const completeStep = useMutation({
    mutationFn: async (
      arg: string | { stepId: string; outcome?: any },
    ) => {
      const stepId = typeof arg === 'string' ? arg : arg.stepId;
      const outcome = typeof arg === 'string' ? null : (arg.outcome ?? null);
      const { error } = await (supabase.rpc as any)('complete_cadence_step', {
        p_step_id: stepId,
        p_outcome: outcome,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-cadence-next', dealId, tenantId] });
      queryClient.invalidateQueries({ queryKey: ['deal-cadence-steps', dealId, tenantId] });
      // v1.1.0: atualiza a timeline (evento 'step_completed' gravado pela RPC)
      invalidateDealTimeline();
      // Garante que a listagem /tenant/activities/list e o feed sejam atualizados
      void invalidateActivityListCaches(queryClient);
    },
  });


  const skipStep = useMutation({
    mutationFn: async ({ stepId, reason }: { stepId: string; reason?: string }) => {
      const { error } = await (supabase.rpc as any)('skip_cadence_step', {
        p_step_id: stepId,
        p_reason: reason ?? null,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-cadence-next', dealId, tenantId] });
      queryClient.invalidateQueries({ queryKey: ['deal-cadence-steps', dealId, tenantId] });
      // v1.1.0: atualiza a timeline (evento 'step_skipped' gravado pela RPC)
      invalidateDealTimeline();
      void invalidateActivityListCaches(queryClient);
    },
  });

  const cancelCadence = useMutation({
    mutationFn: async ({ cadenceId, reason }: { cadenceId: string; reason?: string }) => {
      const { error } = await (supabase.rpc as any)('cancel_deal_cadence', {
        p_cadence_id: cadenceId,
        p_reason: reason ?? null,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-cadence-next', dealId, tenantId] });
      queryClient.invalidateQueries({ queryKey: ['deal-cadence-steps', dealId, tenantId] });
      // v1.1.0: atualiza a timeline (evento 'cadence_cancelled' gravado pela RPC)
      invalidateDealTimeline();
      void invalidateActivityListCaches(queryClient);
    },
  });

  // Reabre um passo previamente concluído/pulado.
  // Backend: RPC reopen_cadence_step (v1.0.0) — valida tenant, restaura
  // status = 'pending' e reativa a cadência-pai se necessário.
  const reopenStep = useMutation({
    mutationFn: async (stepId: string) => {
      const { error } = await (supabase.rpc as any)('reopen_cadence_step', {
        p_step_id: stepId,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-cadence-next', dealId, tenantId] });
      queryClient.invalidateQueries({ queryKey: ['deal-cadence-steps', dealId, tenantId] });
      // v1.1.0: chaves corretas da timeline (antes era ['deal-activities', dealId])
      invalidateDealTimeline();
      void invalidateActivityListCaches(queryClient);
    },
  });


  /**
   * Cancela um step da cadência (v1.1.0). Diferente de "skip": muda status
   * para 'cancelled', registra motivo obrigatório e mantém o histórico visível
   * no calendário. Backend: RPC cancel_cadence_step.
   */
  const cancelStep = useMutation({
    mutationFn: async ({ stepId, reason }: { stepId: string; reason: string }) => {
      const { error } = await (supabase.rpc as any)('cancel_cadence_step', {
        p_step_id: stepId,
        p_reason: reason,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-cadence-next', dealId, tenantId] });
      queryClient.invalidateQueries({ queryKey: ['deal-cadence-steps', dealId, tenantId] });
      // v1.1.0: chaves corretas da timeline (antes era ['deal-activities', dealId])
      invalidateDealTimeline();
      void invalidateActivityListCaches(queryClient);
    },
  });

  /**
   * Cria um step manual dentro da cadência ativa do negócio (v1.1.0).
   * Marca is_manual=true no backend para não distorcer métricas de aderência.
   * Backend: RPC create_manual_cadence_step.
   */
  const createManualStep = useMutation({
    mutationFn: async (input: {
      activityType: string;
      title: string;
      dueAt: string; // ISO
      descriptionHtml?: string | null;
      channel?: string | null;
      phaseId?: string | null;
    }) => {
      if (!dealId) throw new Error('dealId ausente');
      const { data, error } = await (supabase.rpc as any)('create_manual_cadence_step', {
        p_deal_id: dealId,
        p_activity_type: input.activityType,
        p_title: input.title,
        p_due_at: input.dueAt,
        p_description_html: input.descriptionHtml ?? null,
        p_channel: input.channel ?? null,
        p_phase_id: input.phaseId ?? null,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-cadence-next', dealId, tenantId] });
      queryClient.invalidateQueries({ queryKey: ['deal-cadence-steps', dealId, tenantId] });
      // v1.1.0: chaves corretas da timeline (antes era ['deal-activities', dealId])
      invalidateDealTimeline();
      void invalidateActivityListCaches(queryClient);
    },
  });

  /**
   * Edita um step manual pendente da cadência ativa (v1.0.0).
   * Backend: RPC update_manual_cadence_step.
   */
  const updateManualStep = useMutation({
    mutationFn: async (input: {
      stepId: string;
      activityType: string;
      title: string;
      dueAt: string; // ISO
      descriptionHtml?: string | null;
      channel?: string | null;
      phaseId?: string | null;
    }) => {
      const { data, error } = await (supabase.rpc as any)('update_manual_cadence_step', {
        p_step_id: input.stepId,
        p_title: input.title,
        p_activity_type: input.activityType,
        p_due_at: input.dueAt,
        p_description_html: input.descriptionHtml ?? null,
        p_channel: input.channel ?? null,
        p_phase_id: input.phaseId ?? null,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-cadence-next', dealId, tenantId] });
      queryClient.invalidateQueries({ queryKey: ['deal-cadence-steps', dealId, tenantId] });
      // v1.1.0: chaves corretas da timeline (antes era ['deal-activities', dealId])
      invalidateDealTimeline();
      void invalidateActivityListCaches(queryClient);
    },
  });

  /**
   * Ajusta historicamente a data de conclusão de uma atividade (v1.1.0).
   * Aceita step de cadência ('cadence') ou atividade avulsa ('activity').
   * Backend: RPC update_activity_completion_date.
   */
  const editCompletionDate = useMutation({
    mutationFn: async (input: {
      kind: 'cadence' | 'activity';
      id: string;
      completedAt: string; // ISO
    }) => {
      const { error } = await (supabase.rpc as any)('update_activity_completion_date', {
        p_activity_kind: input.kind,
        p_id: input.id,
        p_completed_at: input.completedAt,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-cadence-next', dealId, tenantId] });
      queryClient.invalidateQueries({ queryKey: ['deal-cadence-steps', dealId, tenantId] });
      // v1.1.0: chaves corretas da timeline (antes era ['deal-activities', dealId])
      invalidateDealTimeline();
      void invalidateActivityListCaches(queryClient);
    },
  });


  return {
    nextAction: nextActionQuery.data ?? null,
    steps: stepsQuery.data ?? [],
    isLoading: nextActionQuery.isLoading || stepsQuery.isLoading,
    isError: nextActionQuery.isError || stepsQuery.isError,
    completeStep,
    skipStep,
    cancelCadence,
    reopenStep,
    cancelStep,
    createManualStep,
    updateManualStep,
    editCompletionDate,
  };
}
